import { DicomVRMaxLength } from './dicomConstants';

/**
 * Conformance rules for the DICOM value representations this application
 * writes user input into.
 *
 * One rule rather than a `maxLength` on each input: a length attribute
 * enforces one of the constraints, on one control, and says nothing about
 * the characters a value may hold — a description carrying a backslash is
 * a different problem from a long one, and both reach the exported object.
 * Structure names, structure set labels, series descriptions and export
 * descriptions had four copies of this between them.
 */

/**
 * `IS` (Integer String) holds at most 12 bytes and represents an integer in
 * the signed 32-bit range.
 */
export const INTEGER_STRING_MIN = -2147483648;
export const INTEGER_STRING_MAX = 2147483647;

/**
 * Whether every character of `value` is printable ASCII and none is the
 * backslash — the repertoire a receiver may assume when
 * `SpecificCharacterSet` says nothing, less the multi-value delimiter.
 *
 * The character half of {@link dicomStringViolation}, separately available
 * for callers that care about the characters and not the length.
 */
const PRINTABLE_ASCII = /^[\u0020-\u007e]*$/;

export function hasDicomPrintableCharacters(value: string): boolean {
    return PRINTABLE_ASCII.test(value) && !value.includes('\\');
}

/** Line feed, form feed, carriage return and escape: what a paragraph needs. */
const TEXT_CONTROL_CODES = new Set([0x0a, 0x0c, 0x0d, 0x1b]);

/**
 * Whether every character of `value` can be written into a text VR: ST, LT
 * and UT, which hold paragraphs. The same default repertoire as
 * {@link hasDicomPrintableCharacters}, plus the control characters those
 * VRs carry, and the backslash, which is content where the attribute holds
 * a single value rather than a delimiter.
 */
export function hasDicomTextCharacters(value: string): boolean {
    for (const character of value) {
        const code = character.codePointAt(0) ?? 0;
        if ((code < 0x20 || code > 0x7e) && !TEXT_CONTROL_CODES.has(code)) {
            return false;
        }
    }
    return true;
}

export type DicomStringViolation =
    | 'empty'
    | 'characters'
    | 'backslash'
    | 'length';

/**
 * Why `value` cannot be written into a DICOM string attribute of
 * `maxLength`, or null.
 *
 * For the VRs that forbid the backslash and hold a single line — LO, SH
 * and the like. NOT for ST, LT or UT: those permit the backslash and
 * control characters, and ST's leading spaces are significant, so trimming
 * would alter the value.
 *
 * Printable ASCII only, which is the default character repertoire a
 * receiver may assume when `SpecificCharacterSet` says nothing — the rule
 * this codebase already applies to structure names and to descriptions it
 * imports. Backslash is excluded separately because it IS printable: it is
 * the multi-value delimiter, so a value containing one does not become
 * longer, it becomes two values in an attribute defined to hold one.
 *
 * Leading and trailing spaces are insignificant in a DICOM string, so they
 * are neither counted against the length nor treated as content.
 */
export function dicomStringViolation(
    value: string,
    maxLength: DicomVRMaxLength,
    options: { allowEmpty?: boolean } = {}
): DicomStringViolation | null {
    const trimmed = value.trim();
    if (trimmed === '') {
        return options.allowEmpty ? null : 'empty';
    }
    if (!PRINTABLE_ASCII.test(trimmed)) {
        return 'characters';
    }
    // Reported apart from the rest: it is printable, so "invalid
    // character" reads as a puzzle, and naming it is what lets the user
    // fix it.
    if (trimmed.includes('\\')) {
        return 'backslash';
    }
    if (trimmed.length > maxLength) {
        return 'length';
    }
    return null;
}

/** Whether `value` can be written as an `IS`. */
export function isConformantIntegerString(value: number): boolean {
    return (
        Number.isInteger(value) &&
        value >= INTEGER_STRING_MIN &&
        value <= INTEGER_STRING_MAX
    );
}
